import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { supabase } from "../supabaseClient";
import { useUser } from "../contexts/UserContext";
import Footer from '../components/Footer'
import { motion } from "framer-motion";
import { useReactToPrint } from 'react-to-print';
import { handleMobileFriendlyPrint, isMobileDevice } from '../utils/printHelper';
import {
  ClockIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  CalendarDaysIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

export default function Attendance() {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const printRef = useRef();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [timesheetData, setTimesheetData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);
  const [showAdminEditModal, setShowAdminEditModal] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [selectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'monthly'
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [searchTimesheet, setSearchTimesheet] = useState("");
  const [filterTimesheetDate, setFilterTimesheetDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoadingTimesheet, setIsLoadingTimesheet] = useState(false);
  const [timesheetViewMode, setTimesheetViewMode] = useState('daily'); // 'daily' or 'monthly'
  const [selectedTimesheetMonth, setSelectedTimesheetMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format

  const [checkInNote, setCheckInNote] = useState("");
  const [checkOutNote, setCheckOutNote] = useState("");
  const [checkInLocation, setCheckInLocation] = useState("WFO");
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isSubmittingTimesheet, setIsSubmittingTimesheet] = useState(false);

  const [adminEditForm, setAdminEditForm] = useState({
    date: "",
    check_in: "",
    check_out: "",
    check_in_note: "",
    check_out_note: "",
    work_location: "WFO",
  });

  const [timesheetForm, setTimesheetForm] = useState({
    project_code: "",
    project_name: "",
    task_description: "",
    task_list: [], // Optional list items
    list_mode: false, // Toggle for list mode
    date: new Date().toISOString().split('T')[0],
  });

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");

  // State untuk filter print berdasarkan user
  const [showUserFilterModal, setShowUserFilterModal] = useState(false);
  const [showExportFilterModal, setShowExportFilterModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserForPrint, setSelectedUserForPrint] = useState(null); // null = semua user
  const [, setSelectedUserForExport] = useState(null);
  const [filteredAttendanceForPrint, setFilteredAttendanceForPrint] = useState([]);

  // State untuk modal tambah absensi manual (Admin)
  const [showManualAttendanceModal, setShowManualAttendanceModal] = useState(false);
  const [manualAttendanceForm, setManualAttendanceForm] = useState({
    user_id: "",
    date: new Date().toISOString().split('T')[0],
    check_in: "08:00",
    check_out: "",
    check_in_note: "",
    check_out_note: "",
    work_location: "WFO",
  });

  const showToast = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastMessage("");
      setToastType("");
    }, 3000);
  };

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (userInfo?.id) {
      fetchData();
      // Fetch all users untuk admin (untuk filter print)
      if (userInfo.role === 'admin') {
        fetchAllUsers();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, selectedDate, viewMode, selectedMonth, filterDate]);

  const fetchAllUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("id, full_name, email")
        .eq('status', 'approved')
        .order('full_name', { ascending: true });

      if (error) throw error;
      setAllUsers(data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    await Promise.all([
      fetchTodayAttendance(),
      fetchAttendanceHistory(),
      fetchTimesheetData(),
    ]);
    setIsLoading(false);
  };

  const fetchTodayAttendance = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      // Always fetch current user's attendance for today
      const { data, error } = await supabase
        .from("attendance")
        .select("*")
        .eq("user_id", userInfo.id)
        .eq("date", today)
        .maybeSingle();

      if (error) throw error;
      setTodayAttendance(data);
    } catch (error) {
      console.error("Error fetching today attendance:", error);
    }
  };

  const fetchAttendanceHistory = async () => {
    try {
      // Admin dapat melihat semua data, user biasa hanya data mereka sendiri
      let query = supabase
        .from("attendance")
        .select("*");

      if (userInfo.role !== 'admin') {
        query = query.eq("user_id", userInfo.id);
      }

      query = query.order("date", { ascending: false });

      if (viewMode === 'monthly') {
        // Fetch all data for selected month
        const startOfMonth = `${selectedMonth}-01`;
        const endOfMonth = new Date(selectedMonth + '-01');
        endOfMonth.setMonth(endOfMonth.getMonth() + 1);
        endOfMonth.setDate(0);
        const endDate = endOfMonth.toISOString().split('T')[0];

        query = query.gte("date", startOfMonth).lte("date", endDate);
      } else if (filterDate) {
        // Daily view with specific date filter
        query = query.eq("date", filterDate).limit(30);
      } else {
        // Default: last 30 records
        query = query.limit(30);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Jika admin, fetch user names secara manual
      if (userInfo.role === 'admin' && data && data.length > 0) {
        const userIds = [...new Set(data.map(item => item.user_id))];
        const { data: usersData } = await supabase
          .from("users")
          .select("id, full_name, email")
          .in("id", userIds);

        // Map user data ke attendance
        const attendanceWithUsers = data.map(item => ({
          ...item,
          users: usersData?.find(u => u.id === item.user_id) || null
        }));

        setAttendanceHistory(attendanceWithUsers);
      } else {
        setAttendanceHistory(data || []);
      }
    } catch (error) {
      console.error("Error fetching attendance history:", error);
    }
  };

  const fetchTimesheetData = async () => {
    setIsLoadingTimesheet(true);
    try {
      let query = supabase
        .from("timesheet")
        .select("id, date, project_code, project_name, task_description, created_at")
        .eq("user_id", userInfo.id)
        .order("date", { ascending: false });

      // Apply date filter based on view mode
      if (timesheetViewMode === 'monthly') {
        // Fetch all data for selected month
        const startOfMonth = `${selectedTimesheetMonth}-01`;
        const endOfMonth = new Date(selectedTimesheetMonth + '-01');
        endOfMonth.setMonth(endOfMonth.getMonth() + 1);
        endOfMonth.setDate(0);
        const endDate = endOfMonth.toISOString().split('T')[0];

        query = query.gte("date", startOfMonth).lte("date", endDate);
      } else if (filterTimesheetDate) {
        query = query.eq("date", filterTimesheetDate);
      } else {
        query = query.eq("date", selectedDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Apply search filter on client side
      let filteredData = data || [];
      if (searchTimesheet) {
        const search = searchTimesheet.toLowerCase();
        filteredData = filteredData.filter(item =>
          item.project_code?.toLowerCase().includes(search) ||
          item.project_name?.toLowerCase().includes(search) ||
          item.task_description?.toLowerCase().includes(search)
        );
      }

      setTimesheetData(filteredData);
    } catch (error) {
      console.error("Error fetching timesheet:", error);
    } finally {
      setIsLoadingTimesheet(false);
    }
  };

  // Re-fetch when search or filter changes
  useEffect(() => {
    if (userInfo?.id) {
      fetchTimesheetData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTimesheet, filterTimesheetDate, selectedDate, timesheetViewMode, selectedTimesheetMonth]);

  // Auto-fetch attendance history when filterDate changes
  useEffect(() => {
    if (userInfo?.id && filterDate) {
      fetchAttendanceHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate]);

  const handleCheckIn = async () => {
    if (isCheckingIn) return; // Prevent double click

    try {
      setIsCheckingIn(true);
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const time = now.toTimeString().split(' ')[0];

      // First, check if attendance already exists for today
      const { data: existingData, error: checkError } = await supabase
        .from("attendance")
        .select("*")
        .eq("user_id", userInfo.id)
        .eq("date", today)
        .maybeSingle();

      if (checkError) throw checkError;

      // If record exists, confirm override (terutama untuk admin)
      if (existingData) {
        const confirmMsg = userInfo.role === 'admin'
          ? `Admin: Anda akan override Check In Anda sendiri dari ${existingData.check_in} menjadi ${time}. Lanjutkan?`
          : `Check In sudah ada (${existingData.check_in}). Override dengan waktu sekarang?`;

        if (!window.confirm(confirmMsg)) {
          setIsCheckingIn(false);
          return;
        }

        const { data, error } = await supabase
          .from("attendance")
          .update({
            check_in: time,
            check_in_note: checkInNote || null,
            work_location: checkInLocation,
            updated_at: now.toISOString(),
          })
          .eq("id", existingData.id)
          .select()
          .single();

        if (error) throw error;

        setTodayAttendance(data);
        setCheckInNote("");
        showToast("✅ Check In berhasil diperbarui!");
        fetchAttendanceHistory();
        return;
      }

      // If no record exists, insert new one
      const { data, error } = await supabase
        .from("attendance")
        .insert([{
          user_id: userInfo.id,
          date: today,
          check_in: time,
          check_in_note: checkInNote || null,
          work_location: checkInLocation,
        }])
        .select()
        .single();

      if (error) {
        // Handle duplicate key error specifically
        if (error.code === '23505') {
          showToast("⚠️ Anda sudah check in hari ini. Silakan refresh halaman.", "error");
          await fetchTodayAttendance();
          return;
        }
        throw error;
      }

      setTodayAttendance(data);
      setCheckInNote("");
      showToast("✅ Check In berhasil!");
      fetchAttendanceHistory();
    } catch (error) {
      console.error("Error check in:", error);
      showToast(`❌ Gagal check in: ${error.message}`, "error");
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    if (isCheckingOut) return; // Prevent double click

    try {
      setIsCheckingOut(true);

      // Get today's attendance
      const today = new Date().toISOString().split('T')[0];
      const { data: currentAttendance, error: fetchError } = await supabase
        .from("attendance")
        .select("*")
        .eq("user_id", userInfo.id)
        .eq("date", today)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (!currentAttendance) {
        showToast("⚠️ Anda belum check in hari ini", "error");
        return;
      }

      const now = new Date();
      const time = now.toTimeString().split(' ')[0];

      // Jika sudah ada check out, minta konfirmasi override
      if (currentAttendance.check_out) {
        const confirmMsg = userInfo.role === 'admin'
          ? `Admin: Anda akan override Check Out Anda sendiri dari ${currentAttendance.check_out} menjadi ${time}. Lanjutkan?`
          : `Check Out sudah ada (${currentAttendance.check_out}). Override dengan waktu sekarang?`;

        if (!window.confirm(confirmMsg)) {
          setIsCheckingOut(false);
          return;
        }
      }

      // Update checkout
      const { data, error } = await supabase
        .from("attendance")
        .update({
          check_out: time,
          check_out_note: checkOutNote || null,
          updated_at: now.toISOString(),
        })
        .eq("id", currentAttendance.id)
        .select()
        .single();

      if (error) throw error;

      setTodayAttendance(data);
      setCheckOutNote("");
      showToast("✅ Check Out berhasil!");
      fetchAttendanceHistory();
    } catch (error) {
      console.error("Error check out:", error);
      showToast(`❌ Gagal check out: ${error.message}`, "error");
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleAddTimesheet = async () => {
    if (isSubmittingTimesheet) return; // Prevent double click

    // Validate: either description or list items must exist
    const hasDescription = timesheetForm.task_description?.trim() || '';
    const taskList = timesheetForm.task_list || [];
    const hasListItems = taskList.filter(item => item.trim()).length > 0;

    if (!timesheetForm.project_code || !timesheetForm.project_name || (!hasDescription && !hasListItems)) {
      showToast("Mohon lengkapi semua field", "error");
      return;
    }

    try {
      setIsSubmittingTimesheet(true);
      // Combine description with list items
      let finalDescription = timesheetForm.task_description?.trim() || '';
      const validListItems = taskList.filter(item => item.trim());

      if (validListItems.length > 0) {
        const listText = validListItems.map(item => `• ${item.trim()}`).join('\n');
        finalDescription = finalDescription
          ? `${finalDescription}\n${listText}`
          : listText;
      }

      if (editingTimesheet) {
        // Update existing timesheet
        const { error } = await supabase
          .from("timesheet")
          .update({
            project_code: timesheetForm.project_code,
            project_name: timesheetForm.project_name,
            task_description: finalDescription,
            date: timesheetForm.date,
          })
          .eq("id", editingTimesheet.id);

        if (error) throw error;
        showToast("✅ Timesheet berhasil diupdate");
      } else {
        // Insert new timesheet
        const { error } = await supabase
          .from("timesheet")
          .insert([{
            user_id: userInfo.id,
            date: timesheetForm.date,
            project_code: timesheetForm.project_code,
            project_name: timesheetForm.project_name,
            task_description: finalDescription,
          }]);

        if (error) throw error;
        showToast("✅ Timesheet berhasil ditambahkan");
      }

      setShowTimesheetModal(false);
      setEditingTimesheet(null);
      setTimesheetForm({
        project_code: "",
        project_name: "",
        task_description: "",
        task_list: [],
        list_mode: false,
        date: new Date().toISOString().split('T')[0]
      });
      fetchTimesheetData();
    } catch (error) {
      console.error("Error saving timesheet:", error);
      showToast("Gagal menyimpan timesheet", "error");
    } finally {
      setIsSubmittingTimesheet(false);
    }
  };

  const handleEditTimesheet = (item) => {
    setEditingTimesheet(item);
    setTimesheetForm({
      project_code: item.project_code,
      project_name: item.project_name,
      task_description: item.task_description,
      date: item.date,
    });
    setShowTimesheetModal(true);
  };

  const handleDeleteTimesheet = async (id) => {
    if (!window.confirm("Yakin ingin menghapus data timesheet ini?")) return;

    try {
      const { error } = await supabase
        .from("timesheet")
        .delete()
        .eq("id", id);

      if (error) throw error;

      fetchTimesheetData();
      showToast("✅ Timesheet berhasil dihapus");
    } catch (error) {
      console.error("Error deleting timesheet:", error);
      showToast("Gagal menghapus timesheet", "error");
    }
  };

  const handleDeleteAttendance = async (id) => {
    if (!window.confirm("Yakin ingin menghapus data absensi ini?")) return;

    try {
      const { error } = await supabase
        .from("attendance")
        .delete()
        .eq("id", id);

      if (error) throw error;

      fetchAttendanceHistory();
      fetchTodayAttendance(); // Auto refresh today's attendance
      showToast("✅ Absensi berhasil dihapus");
    } catch (error) {
      console.error("Error deleting attendance:", error);
      showToast("Gagal menghapus absensi", "error");
    }
  };

  const handleEditAttendance = (item) => {
    setEditingAttendance(item);
    setAdminEditForm({
      date: item.date,
      check_in: item.check_in || "",
      check_out: item.check_out || "",
      check_in_note: item.check_in_note || "",
      check_out_note: item.check_out_note || "",
      work_location: item.work_location || "WFO",
    });
    setShowAdminEditModal(true);
  };

  const handleSaveAdminEdit = async () => {
    if (!adminEditForm.check_in) {
      showToast("Check in wajib diisi", "error");
      return;
    }

    try {
      // Konfirmasi jika admin mengedit data user lain
      if (editingAttendance.user_id !== userInfo.id) {
        const userName = editingAttendance.users?.full_name || 'User lain';
        if (!window.confirm(`⚠️ Anda akan mengedit data absensi ${userName}. Lanjutkan?`)) {
          return;
        }
      }

      const { error } = await supabase
        .from("attendance")
        .update({
          check_in: adminEditForm.check_in,
          check_out: adminEditForm.check_out || null,
          check_in_note: adminEditForm.check_in_note || null,
          check_out_note: adminEditForm.check_out_note || null,
          work_location: adminEditForm.work_location,
        })
        .eq("id", editingAttendance.id);

      if (error) throw error;

      setShowAdminEditModal(false);
      setEditingAttendance(null);
      fetchAttendanceHistory();
      fetchTodayAttendance();
      showToast("✅ Data absensi berhasil diupdate");
    } catch (error) {
      console.error("Error updating attendance:", error);
      showToast("Gagal mengupdate absensi", "error");
    }
  };

  // Handle tambah absensi manual (Admin)
  const handleOpenManualAttendance = () => {
    setManualAttendanceForm({
      user_id: userInfo?.id || "",
      date: new Date().toISOString().split('T')[0],
      check_in: "08:00",
      check_out: "",
      check_in_note: "",
      check_out_note: "",
      work_location: "WFO",
    });
    setShowManualAttendanceModal(true);
  };

  const handleSaveManualAttendance = async () => {
    if (!manualAttendanceForm.user_id) {
      showToast("Pilih user terlebih dahulu", "error");
      return;
    }
    if (!manualAttendanceForm.date) {
      showToast("Tanggal wajib diisi", "error");
      return;
    }
    if (!manualAttendanceForm.check_in) {
      showToast("Jam masuk wajib diisi", "error");
      return;
    }

    try {
      // Cek apakah sudah ada absensi di tanggal tersebut untuk user yang dipilih
      const { data: existingData, error: checkError } = await supabase
        .from("attendance")
        .select("id, check_in")
        .eq("user_id", manualAttendanceForm.user_id)
        .eq("date", manualAttendanceForm.date)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      const selectedUser = allUsers.find(u => u.id === manualAttendanceForm.user_id);
      const userName = selectedUser?.full_name || 'User';

      if (existingData) {
        // Sudah ada data, konfirmasi override
        if (!window.confirm(`⚠️ ${userName} sudah memiliki absensi pada tanggal ${manualAttendanceForm.date} (Check In: ${existingData.check_in}). Override data tersebut?`)) {
          return;
        }

        // Update existing
        const { error: updateError } = await supabase
          .from("attendance")
          .update({
            check_in: manualAttendanceForm.check_in,
            check_out: manualAttendanceForm.check_out || null,
            check_in_note: manualAttendanceForm.check_in_note || null,
            check_out_note: manualAttendanceForm.check_out_note || null,
            work_location: manualAttendanceForm.work_location,
          })
          .eq("id", existingData.id);

        if (updateError) throw updateError;
        showToast(`✅ Data absensi ${userName} berhasil diupdate`);
      } else {
        // Insert new
        const { error: insertError } = await supabase
          .from("attendance")
          .insert([{
            user_id: manualAttendanceForm.user_id,
            date: manualAttendanceForm.date,
            check_in: manualAttendanceForm.check_in,
            check_out: manualAttendanceForm.check_out || null,
            check_in_note: manualAttendanceForm.check_in_note || null,
            check_out_note: manualAttendanceForm.check_out_note || null,
            work_location: manualAttendanceForm.work_location,
          }]);

        if (insertError) throw insertError;
        showToast(`✅ Absensi manual ${userName} berhasil ditambahkan`);
      }

      setShowManualAttendanceModal(false);
      fetchAttendanceHistory();
      fetchTodayAttendance();
    } catch (error) {
      console.error("Error saving manual attendance:", error);
      showToast("Gagal menyimpan absensi manual: " + error.message, "error");
    }
  };

  const handleExportExcel = () => {
    // Jika admin, tampilkan modal untuk pilih user
    if (userInfo?.role === 'admin') {
      setShowExportFilterModal(true);
    } else {
      // Non-admin langsung export
      executeExport(null);
    }
  };

  const executeExport = (user) => {
    // Filter data berdasarkan user yang dipilih
    let dataForExport = attendanceHistory;
    if (user) {
      dataForExport = attendanceHistory.filter(item => item.user_id === user.id);
    }

    // Tentukan nama user untuk export
    const exportUserName = user?.full_name || userInfo?.full_name || 'All Users';
    const exportUserRole = user?.position || userInfo?.position || 'Employee';
    const exportUserDept = user?.department || userInfo?.department || 'General';

    // Hitung periode dari data
    const dates = dataForExport.map(item => new Date(item.date)).sort((a, b) => a - b);
    const startDate = dates.length > 0 ? dates[0].toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-';
    const endDate = dates.length > 0 ? dates[dates.length - 1].toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-';
    const periode = `${startDate} - ${endDate}`;

    // Buat header rows
    const headerRows = [
      ['', '', 'PT ADI DATA INFORMATIKA - Daily Attendance Report', '', '', '', '', '', ''],
      [''],
      [`Name: ${exportUserName}`],
      [`Role: ${exportUserRole}`],
      [`Department: ${exportUserDept}`],
      [`Period: ${periode}`],
      [''],
    ];

    // Kolom header tabel
    const tableHeader = userInfo?.role === 'admin' && !user
      ? ['No', 'Nama', 'Hari', 'Tanggal', 'Check In', 'Check Out', 'Durasi', 'Lokasi', 'Keterangan']
      : ['No', 'Hari', 'Tanggal', 'Check In', 'Check Out', 'Durasi', 'Lokasi', 'Keterangan In', 'Keterangan Out'];

    // Data rows
    const dataRows = dataForExport.map((item, index) => {
      const date = new Date(item.date);
      const hari = date.toLocaleDateString('id-ID', { weekday: 'long' });
      const tanggal = date.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      const durasi = calculateDuration(item.check_in, item.check_out);

      if (userInfo?.role === 'admin' && !user) {
        return [
          index + 1,
          item.users?.full_name || 'N/A',
          hari,
          tanggal,
          item.check_in || '-',
          item.check_out || '-',
          durasi,
          item.work_location || '-',
          item.check_in_note || item.check_out_note || '-'
        ];
      }

      return [
        index + 1,
        hari,
        tanggal,
        item.check_in || '-',
        item.check_out || '-',
        durasi,
        item.work_location || '-',
        item.check_in_note || '-',
        item.check_out_note || '-'
      ];
    });

    // Summary row
    const totalDays = dataForExport.length;
    const totalPresent = dataForExport.filter(item => item.check_in).length;
    const summaryRows = [
      [''],
      [`Total Hari Kerja: ${totalDays}`, '', `Total Hadir: ${totalPresent}`, '', '', '', '', '', ''],
      [''],
      ['Report generated on:', new Date().toLocaleString('id-ID'), '', '', '', '', '', '', ''],
    ];

    // Gabungkan semua rows
    const allRows = [
      ...headerRows,
      tableHeader,
      ...dataRows,
      ...summaryRows
    ];

    // Buat worksheet
    const ws = XLSX.utils.aoa_to_sheet(allRows);

    // Set column widths
    ws['!cols'] = [
      { wch: 5 },   // No
      { wch: 20 },  // Nama/Hari
      { wch: 15 },  // Hari/Tanggal
      { wch: 20 },  // Tanggal/Check In
      { wch: 10 },  // Check In/Out
      { wch: 10 },  // Check Out/Durasi
      { wch: 10 },  // Durasi/Lokasi
      { wch: 10 },  // Lokasi/Ket In
      { wch: 25 },  // Keterangan
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance Report");

    const fileName = user
      ? `Attendance_Report_${user.full_name}_${new Date().toISOString().split('T')[0]}.xlsx`
      : (userInfo?.role === 'admin'
        ? `Attendance_Report_All_Users_${new Date().toISOString().split('T')[0]}.xlsx`
        : `Attendance_Report_${userInfo.full_name}_${new Date().toISOString().split('T')[0]}.xlsx`);

    XLSX.writeFile(wb, fileName);

    setShowExportFilterModal(false);
    setSelectedUserForExport(null);
    showToast("📊 Laporan berhasil diexport ke Excel");
  };

  const handlePrint = () => {
    // Jika admin, tampilkan modal untuk pilih user
    if (userInfo?.role === 'admin') {
      setShowUserFilterModal(true);
    } else {
      // Non-admin langsung print
      executePrint(null);
    }
  };

  const executePrint = (user) => {
    // Filter data berdasarkan user yang dipilih
    let dataForPrint = attendanceHistory;
    if (user) {
      dataForPrint = attendanceHistory.filter(item => item.user_id === user.id);
    }

    // Set state untuk print
    setFilteredAttendanceForPrint(dataForPrint);
    setSelectedUserForPrint(user);

    // Execute print dengan delay untuk memastikan state terupdate
    setTimeout(() => {
      if (isMobileDevice()) {
        showToast('💡 Tip: Untuk hasil terbaik di mobile, gunakan Export Excel', 'info');
        setTimeout(() => {
          handleMobileFriendlyPrint({
            documentTitle: user
              ? `Attendance_${user.full_name}`
              : `Attendance_All_Users`,
            onAfterPrint: () => {
              showToast('Dokumen berhasil dicetak');
              setShowUserFilterModal(false);
              setSelectedUserForPrint(null);
              setFilteredAttendanceForPrint([]);
            }
          });
        }, 1000);
      } else {
        handleDesktopPrint();
      }
    }, 100);
  };

  const handleDesktopPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: selectedUserForPrint
      ? `Attendance_${selectedUserForPrint.full_name}`
      : (userInfo?.role === 'admin' ? 'Attendance_All_Users' : `Attendance_${userInfo?.full_name}`),
    onAfterPrint: () => {
      showToast('Dokumen berhasil dicetak');
      setShowUserFilterModal(false);
      setSelectedUserForPrint(null);
      setFilteredAttendanceForPrint([]);
    }
  });

  const calculateDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return "-";

    const [inH, inM] = checkIn.split(':').map(Number);
    const [outH, outM] = checkOut.split(':').map(Number);

    const totalMinutes = (outH * 60 + outM) - (inH * 60 + inM);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}j ${minutes}m`;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("id-ID", {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("id-ID", {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      {/* Modal Tambah Absensi Manual (Admin Only) - Using Portal */}
      {showManualAttendanceModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}
          onClick={() => setShowManualAttendanceModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Fixed */}
            <div className="flex-shrink-0 bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 px-4 py-3 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <h2 className="text-base font-bold text-white">
                    Tambah Absensi Manual
                  </h2>
                </div>
                <button
                  onClick={() => setShowManualAttendanceModal(false)}
                  className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-blue-700">
                    <p className="font-semibold">Fitur Admin</p>
                    <p className="mt-1">Gunakan fitur ini untuk input absensi karyawan yang lupa absen.</p>
                  </div>
                </div>
              </div>

              {/* Pilih User */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Pilih Karyawan <span className="text-red-500">*</span>
                </label>
                <select
                  value={manualAttendanceForm.user_id}
                  onChange={(e) => setManualAttendanceForm({ ...manualAttendanceForm, user_id: e.target.value })}
                  className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                >
                  <option value="">-- Pilih Karyawan --</option>
                  {allUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} {user.id === userInfo?.id ? '(Anda)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tanggal - menggunakan dropdown */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tanggal <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {/* Hari */}
                  <select
                    value={manualAttendanceForm.date?.split('-')[2] || ''}
                    onChange={(e) => {
                      const parts = (manualAttendanceForm.date || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`).split('-');
                      parts[2] = e.target.value.padStart(2, '0');
                      setManualAttendanceForm({ ...manualAttendanceForm, date: parts.join('-') });
                    }}
                    className="w-full px-2 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                  >
                    <option value="">Tgl</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                    ))}
                  </select>
                  {/* Bulan */}
                  <select
                    value={manualAttendanceForm.date?.split('-')[1] || ''}
                    onChange={(e) => {
                      const parts = (manualAttendanceForm.date || `${new Date().getFullYear()}-01-${String(new Date().getDate()).padStart(2, '0')}`).split('-');
                      parts[1] = e.target.value;
                      setManualAttendanceForm({ ...manualAttendanceForm, date: parts.join('-') });
                    }}
                    className="w-full px-2 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                  >
                    <option value="">Bln</option>
                    {['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'].map((m, i) => (
                      <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
                    ))}
                  </select>
                  {/* Tahun */}
                  <select
                    value={manualAttendanceForm.date?.split('-')[0] || ''}
                    onChange={(e) => {
                      const parts = (manualAttendanceForm.date || `2026-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`).split('-');
                      parts[0] = e.target.value;
                      setManualAttendanceForm({ ...manualAttendanceForm, date: parts.join('-') });
                    }}
                    className="w-full px-2 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                  >
                    <option value="">Thn</option>
                    {[2024, 2025, 2026].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1.5">Pilih tanggal, bulan, dan tahun</p>
              </div>

              {/* Jam Masuk & Keluar - menggunakan dropdown */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Jam Masuk <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-1 items-center">
                    <select
                      value={manualAttendanceForm.check_in?.split(':')[0] || ''}
                      onChange={(e) => {
                        const minute = manualAttendanceForm.check_in?.split(':')[1] || '00';
                        setManualAttendanceForm({ ...manualAttendanceForm, check_in: `${e.target.value}:${minute}` });
                      }}
                      className="flex-1 px-2 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                    >
                      <option value="">--</option>
                      {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <span className="text-gray-500 font-bold">:</span>
                    <select
                      value={manualAttendanceForm.check_in?.split(':')[1] || ''}
                      onChange={(e) => {
                        const hour = manualAttendanceForm.check_in?.split(':')[0] || '08';
                        setManualAttendanceForm({ ...manualAttendanceForm, check_in: `${hour}:${e.target.value}` });
                      }}
                      className="flex-1 px-2 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                    >
                      <option value="">--</option>
                      {['00', '15', '30', '45'].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Jam Keluar
                  </label>
                  <div className="flex gap-1 items-center">
                    <select
                      value={manualAttendanceForm.check_out?.split(':')[0] || ''}
                      onChange={(e) => {
                        const minute = manualAttendanceForm.check_out?.split(':')[1] || '00';
                        setManualAttendanceForm({ ...manualAttendanceForm, check_out: e.target.value ? `${e.target.value}:${minute}` : '' });
                      }}
                      className="flex-1 px-2 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                    >
                      <option value="">--</option>
                      {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <span className="text-gray-500 font-bold">:</span>
                    <select
                      value={manualAttendanceForm.check_out?.split(':')[1] || ''}
                      onChange={(e) => {
                        const hour = manualAttendanceForm.check_out?.split(':')[0] || '17';
                        setManualAttendanceForm({ ...manualAttendanceForm, check_out: hour ? `${hour}:${e.target.value}` : '' });
                      }}
                      className="flex-1 px-2 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                    >
                      <option value="">--</option>
                      {['00', '15', '30', '45'].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Lokasi Kerja */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Lokasi Kerja
                </label>
                <div className="flex gap-3">
                  {['WFO', 'WFH', 'WFA'].map((loc) => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => setManualAttendanceForm({ ...manualAttendanceForm, work_location: loc })}
                      className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${manualAttendanceForm.work_location === loc
                        ? 'bg-emerald-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      {loc}
                    </button>
                  ))}
                </div>
              </div>

              {/* Catatan Check In */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Catatan Check-in
                </label>
                <textarea
                  value={manualAttendanceForm.check_in_note}
                  onChange={(e) => setManualAttendanceForm({ ...manualAttendanceForm, check_in_note: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none transition-all"
                  rows="2"
                  placeholder="Contoh: Lupa absen karena meeting pagi..."
                />
              </div>

              {/* Catatan Check Out */}
              <div className="pb-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Catatan Check-out
                </label>
                <textarea
                  value={manualAttendanceForm.check_out_note}
                  onChange={(e) => setManualAttendanceForm({ ...manualAttendanceForm, check_out_note: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none transition-all"
                  rows="2"
                  placeholder="Opsional..."
                />
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className="flex-shrink-0 bg-gray-50 px-5 py-4 rounded-b-2xl border-t border-gray-200">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowManualAttendanceModal(false)}
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all text-sm"
                >
                  Batal
                </button>
                <button
                  onClick={handleSaveManualAttendance}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all shadow-md hover:shadow-lg text-sm"
                >
                  💾 Simpan Absensi
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="min-h-screen bg-white relative overflow-hidden flex flex-col">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="relative inline-block">
                <div className="w-16 h-16 relative">
                  <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-blue-500 border-r-blue-500 animate-spin"></div>
                  <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-b-blue-500 border-l-pink-500 animate-spin-reverse"></div>
                  <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 animate-pulse shadow-lg"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full shadow-md animate-ping"></div>
                  </div>
                </div>
              </div>
              <p className="mt-6 text-gray-700 font-medium text-base animate-pulse">Loading attendance data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100/30 via-blue-100/30 to-blue-100/30 rounded-full blur-3xl animate-float"></div>
              <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-blue-100/30 via-pink-100/30 to-blue-100/30 rounded-full blur-3xl animate-float-delayed"></div>
            </div>

            <main className="relative z-10 p-4 sm:p-6 lg:p-8 flex-1">
              <div className="max-w-7xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Header */}
                  <div className="relative backdrop-blur-xl bg-gradient-to-r from-blue-600/90 via-blue-600/90 to-blue-600/90 p-4 sm:p-6 lg:p-8 text-white rounded-t-2xl sm:rounded-t-3xl shadow-xl border border-white/20 overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 animate-pulse"></div>

                    <div className="relative z-10">
                      <div className="flex justify-between items-start sm:items-center flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
                          <button
                            onClick={() => navigate("/dashboard")}
                            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 rounded-lg shadow-sm transition-all duration-200 group flex-shrink-0"
                          >
                            <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform" />
                          </button>
                          <div className="flex-1">
                            <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold tracking-tight">
                              Absensi Harian
                            </h1>
                            <p className="text-blue-100 text-xs sm:text-sm mt-0.5 sm:mt-1 flex items-center gap-1.5 sm:gap-2">
                              <ClockIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              <span>{formatDate(currentTime)}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto">
                          <button
                            onClick={handleExportExcel}
                            className="flex items-center justify-center gap-1.5 sm:gap-2 bg-green-600 hover:bg-green-700 active:scale-95 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl shadow-md hover:shadow-lg font-semibold transition-all duration-200 text-sm sm:text-base flex-1 sm:flex-initial min-h-[44px]"
                          >
                            <ArrowDownTrayIcon className="w-5 h-5" />
                            <span>Export</span>
                          </button>
                          <button
                            onClick={handlePrint}
                            className="flex items-center justify-center gap-1.5 sm:gap-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl shadow-md hover:shadow-lg font-semibold transition-all duration-200 text-sm sm:text-base flex-1 sm:flex-initial min-h-[44px]"
                          >
                            <PrinterIcon className="w-5 h-5" />
                            <span>Print</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="bg-white rounded-b-2xl shadow-xl p-4 sm:p-6 lg:p-8">
                    {/* Live Clock & Check In/Out Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
                      {/* Live Clock */}
                      <div className="bg-gradient-to-br from-blue-50 to-blue-50 p-4 sm:p-5 lg:p-6 rounded-xl sm:rounded-2xl border border-blue-200">
                        <div className="text-center">
                          <p className="text-xs sm:text-sm text-slate-600 mb-1.5 sm:mb-2">Waktu Sekarang</p>
                          <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-600 mb-1.5 sm:mb-2 font-mono">
                            {formatTime(currentTime)}
                          </div>
                          <p className="text-xs sm:text-sm text-slate-600">{formatDate(currentTime)}</p>
                        </div>
                      </div>

                      {/* Check In/Out Status */}
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 sm:p-5 lg:p-6 rounded-xl sm:rounded-2xl border border-green-200">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-slate-700">Status Hari Ini:</span>
                            {todayAttendance ? (
                              todayAttendance.check_out ? (
                                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                                  ✓ Selesai
                                </span>
                              ) : (
                                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                                  ● Sedang Bekerja
                                </span>
                              )
                            ) : (
                              <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                                - Belum Check In
                              </span>
                            )}
                          </div>

                          {todayAttendance && (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-600">Check In:</span>
                                <span className="font-semibold text-slate-800">{todayAttendance.check_in}</span>
                              </div>
                              {todayAttendance.check_out && (
                                <>
                                  <div className="flex justify-between">
                                    <span className="text-slate-600">Check Out:</span>
                                    <span className="font-semibold text-slate-800">{todayAttendance.check_out}</span>
                                  </div>
                                  <div className="flex justify-between pt-2 border-t border-green-200">
                                    <span className="text-slate-600">Durasi:</span>
                                    <span className="font-bold text-green-600">
                                      {calculateDuration(todayAttendance.check_in, todayAttendance.check_out)}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Check In/Out Buttons */}
                    {!todayAttendance ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6 mb-6 sm:mb-8">
                        <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-3 sm:mb-4">Check In Hari Ini</h3>
                        <div className="space-y-3 sm:space-y-4">
                          <div>
                            <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2 block">Lokasi Kerja</label>
                            <select
                              value={checkInLocation}
                              onChange={(e) => setCheckInLocation(e.target.value)}
                              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 lg:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                            >
                              <option value="WFO">WFO (Work From Office)</option>
                              <option value="WFH">WFH (Work From Home)</option>
                              <option value="WFA">WFA (Work From Anywhere)</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2 block">
                              Keterangan (Opsional)
                            </label>
                            <input
                              type="text"
                              value={checkInNote}
                              onChange={(e) => setCheckInNote(e.target.value)}
                              placeholder="Contoh: Izin datang terlambat karena..."
                              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 lg:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                            />
                          </div>
                          <button
                            onClick={handleCheckIn}
                            disabled={isCheckingIn}
                            className="w-full flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 active:scale-[0.98] text-white px-5 sm:px-6 py-3.5 sm:py-4 rounded-xl shadow-lg hover:shadow-xl text-base sm:text-lg font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[52px]"
                          >
                            {isCheckingIn ? (
                              <>
                                <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Memproses...
                              </>
                            ) : (
                              <>
                                <CheckCircleIcon className="w-6 h-6" />
                                Check In Sekarang
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : todayAttendance && !todayAttendance.check_out ? (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6 mb-6 sm:mb-8">
                        <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-3 sm:mb-4">Check Out</h3>
                        <div className="space-y-3 sm:space-y-4">
                          <div>
                            <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2 block">
                              Keterangan (Opsional)
                            </label>
                            <input
                              type="text"
                              value={checkOutNote}
                              onChange={(e) => setCheckOutNote(e.target.value)}
                              placeholder="Contoh: Pulang lebih awal untuk..."
                              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 lg:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                            />
                          </div>
                          <button
                            onClick={handleCheckOut}
                            disabled={isCheckingOut}
                            className="w-full flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 active:scale-[0.98] text-white px-5 sm:px-6 py-3.5 sm:py-4 rounded-xl shadow-lg hover:shadow-xl text-base sm:text-lg font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[52px]"
                          >
                            {isCheckingOut ? (
                              <>
                                <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Memproses...
                              </>
                            ) : (
                              <>
                                <XCircleIcon className="w-6 h-6" />
                                Check Out Sekarang
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : todayAttendance && todayAttendance.check_out && userInfo?.role === 'admin' ? (
                      <div className="bg-gradient-to-r from-blue-50 to-blue-50 border-2 border-blue-200 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
                        <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 text-center">Admin: Override Absensi</h3>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          <button
                            onClick={handleCheckIn}
                            className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:scale-95 text-white px-5 py-3.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 min-h-[48px]"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                            </svg>
                            Check In
                          </button>
                          <button
                            onClick={handleCheckOut}
                            className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:scale-95 text-white px-5 py-3.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 min-h-[48px]"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Check Out
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {/* Timesheet Section */}
                    <div className="mb-6 sm:mb-8">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3 sm:mb-4">
                        <h3 className="text-lg sm:text-xl font-bold text-slate-800 flex items-center gap-2">
                          <BriefcaseIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                          Data Timesheet
                        </h3>
                        <button
                          onClick={() => {
                            setEditingTimesheet(null);
                            setTimesheetForm({
                              project_code: "",
                              project_name: "",
                              task_description: "",
                              task_list: [],
                              list_mode: false,
                              date: new Date().toISOString().split('T')[0]
                            });
                            setShowTimesheetModal(true);
                          }}
                          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-bold shadow-md hover:shadow-lg transition-all w-full sm:w-auto min-h-[44px]"
                        >
                          <CalendarDaysIcon className="w-5 h-5" />
                          <span>Tambah Timesheet</span>
                        </button>
                      </div>

                      {/* Search and Filter - Responsive */}
                      <div className="space-y-2 mb-3 sm:mb-4">
                        {/* View Mode Toggle */}
                        <div className="flex gap-2 mb-2">
                          <button
                            onClick={() => setTimesheetViewMode('daily')}
                            disabled={isLoadingTimesheet}
                            className={`flex-1 py-2 px-3 rounded-xl text-sm font-semibold transition-all ${timesheetViewMode === 'daily'
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              } ${isLoadingTimesheet ? 'opacity-70 cursor-not-allowed' : ''}`}
                          >
                            Harian
                          </button>
                          <button
                            onClick={() => setTimesheetViewMode('monthly')}
                            disabled={isLoadingTimesheet}
                            className={`flex-1 py-2 px-3 rounded-xl text-sm font-semibold transition-all ${timesheetViewMode === 'monthly'
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              } ${isLoadingTimesheet ? 'opacity-70 cursor-not-allowed' : ''}`}
                          >
                            Bulanan
                          </button>
                        </div>

                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                              type="text"
                              value={searchTimesheet}
                              onChange={(e) => setSearchTimesheet(e.target.value)}
                              placeholder="Cari kode project..."
                              className="w-full pl-10 pr-3 py-2.5 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 min-h-[44px]"
                            />
                          </div>
                          {searchTimesheet && (
                            <button
                              onClick={() => setSearchTimesheet("")}
                              className="px-3 py-2.5 bg-gray-200 hover:bg-gray-300 active:scale-95 text-gray-700 rounded-xl text-sm font-bold transition-all flex-shrink-0 shadow-sm min-h-[44px]"
                            >
                              Reset
                            </button>
                          )}
                        </div>

                        {/* Date/Month Filter based on view mode */}
                        <div className="relative">
                          {isLoadingTimesheet ? (
                            <div className="w-5 h-5 absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin z-10 hidden sm:block"></div>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                          {timesheetViewMode === 'monthly' ? (
                            <input
                              type="month"
                              value={selectedTimesheetMonth}
                              onChange={(e) => setSelectedTimesheetMonth(e.target.value)}
                              disabled={isLoadingTimesheet}
                              className={`w-full pl-2.5 sm:pl-10 pr-2.5 py-2.5 text-xs sm:text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 bg-white hover:bg-blue-50 transition-all cursor-pointer min-h-[42px] sm:min-h-[44px] ${isLoadingTimesheet ? 'opacity-70 cursor-not-allowed' : ''}`}
                              style={{ colorScheme: 'light' }}
                            />
                          ) : (
                            <input
                              type="date"
                              value={filterTimesheetDate}
                              onChange={(e) => setFilterTimesheetDate(e.target.value)}
                              disabled={isLoadingTimesheet}
                              className={`w-full pl-2.5 sm:pl-10 pr-2.5 py-2.5 text-xs sm:text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 bg-white hover:bg-blue-50 transition-all cursor-pointer min-h-[42px] sm:min-h-[44px] ${isLoadingTimesheet ? 'opacity-70 cursor-not-allowed' : ''}`}
                              style={{ colorScheme: 'light' }}
                            />
                          )}
                          {isLoadingTimesheet ? (
                            <div className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin sm:hidden"></div>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                        </div>
                      </div>

                      <div className="overflow-x-auto rounded-xl border border-gray-200">
                        <table className="w-full">
                          <thead className="bg-gradient-to-r from-blue-50 to-blue-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Tanggal</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Kode Project</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Nama Project</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Task</th>
                              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Aksi</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {isLoadingTimesheet ? (
                              <tr>
                                <td colSpan="5" className="px-4 py-8 text-center">
                                  <div className="flex flex-col items-center justify-center gap-3">
                                    <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                                    <span className="text-sm text-slate-500">Memuat data timesheet...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : timesheetData.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                                  Project masih kosong
                                </td>
                              </tr>
                            ) : (
                              timesheetData.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm">{new Date(item.date).toLocaleDateString("id-ID")}</td>
                                  <td className="px-4 py-3 text-sm font-mono font-semibold text-blue-600">
                                    {item.project_code}
                                  </td>
                                  <td className="px-4 py-3 text-sm">{item.project_name}</td>
                                  <td className="px-4 py-3 text-sm whitespace-pre-line">{item.task_description}</td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center justify-center gap-2">
                                      <button
                                        onClick={() => handleEditTimesheet(item)}
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Edit"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => handleDeleteTimesheet(item.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Hapus"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Riwayat Absensi */}
                    <div>
                      <div className="mb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                          <h3 className="text-xl font-bold text-slate-800">Riwayat Absensi</h3>

                          {/* Tombol Tambah Absensi Manual (Admin Only) */}
                          {userInfo?.role === 'admin' && (
                            <button
                              onClick={handleOpenManualAttendance}
                              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span>Tambah Absensi Manual</span>
                            </button>
                          )}
                        </div>

                        <div className="space-y-2">
                          {/* View Mode Toggle */}
                          <div className="flex bg-gray-100 rounded-lg p-1 min-h-[44px] items-center">
                            <button
                              onClick={() => setViewMode('daily')}
                              className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all min-h-[36px] ${viewMode === 'daily'
                                ? 'bg-white text-blue-600 shadow-md'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                            >
                              Harian
                            </button>
                            <button
                              onClick={() => setViewMode('monthly')}
                              className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all min-h-[36px] ${viewMode === 'monthly'
                                ? 'bg-white text-blue-600 shadow-md'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                            >
                              Bulanan
                            </button>
                          </div>

                          {/* Date/Month Filter */}
                          <div className="flex gap-2">
                            {viewMode === 'daily' ? (
                              <div className="relative flex-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <input
                                  type="date"
                                  value={filterDate}
                                  onChange={(e) => setFilterDate(e.target.value)}
                                  className="w-full pl-2.5 sm:pl-10 pr-2.5 py-2.5 text-xs sm:text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 bg-white hover:bg-blue-50 transition-all cursor-pointer min-h-[42px] sm:min-h-[44px]"
                                  style={{ colorScheme: 'light' }}
                                />
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            ) : (
                              <div className="relative flex-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <input
                                  type="month"
                                  value={selectedMonth}
                                  onChange={(e) => setSelectedMonth(e.target.value)}
                                  className="w-full pl-10 pr-10 py-2.5 border-2 border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-400 bg-white hover:bg-blue-50 transition-all cursor-pointer min-h-[44px]"
                                  style={{ colorScheme: 'light' }}
                                />
                              </div>
                            )}

                            {viewMode === 'daily' && filterDate !== new Date().toISOString().split('T')[0] && (
                              <button
                                onClick={() => setFilterDate(new Date().toISOString().split('T')[0])}
                                className="px-3 py-2.5 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 active:scale-95 text-gray-700 rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md min-h-[44px] flex-shrink-0"
                                title="Reset ke hari ini"
                              >
                                Reset
                              </button>
                            )}
                          </div>

                          {viewMode === 'monthly' && (
                            <div className="text-sm text-gray-600 font-semibold px-3 py-2 bg-blue-50 rounded-lg text-center">
                              {attendanceHistory.length} hari tercatat
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
                        <table className="w-full">
                          <thead className="bg-gradient-to-r from-blue-50 to-blue-50">
                            <tr>
                              {userInfo?.role === 'admin' && (
                                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Nama</th>
                              )}
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Tanggal</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Check In</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Check Out</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Durasi</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Lokasi</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Keterangan</th>
                              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Aksi</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {attendanceHistory.map((item) => (
                              <tr key={item.id} className="hover:bg-gray-50">
                                {userInfo?.role === 'admin' && (
                                  <td className="px-4 py-3 text-sm">
                                    <div>
                                      <p className="font-semibold text-slate-800">{item.users?.full_name || 'N/A'}</p>
                                      <p className="text-xs text-slate-500">{item.users?.email || ''}</p>
                                    </div>
                                  </td>
                                )}
                                <td className="px-4 py-3 text-sm">
                                  {new Date(item.date).toLocaleDateString("id-ID", { weekday: 'short', day: 'numeric', month: 'short' })}
                                </td>
                                <td className="px-4 py-3 text-sm font-semibold text-green-600">{item.check_in || "-"}</td>
                                <td className="px-4 py-3 text-sm font-semibold text-red-600">{item.check_out || "-"}</td>
                                <td className="px-4 py-3 text-sm font-bold text-blue-600">
                                  {calculateDuration(item.check_in, item.check_out)}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.work_location === 'WFO' ? 'bg-blue-100 text-blue-800' : 'bg-blue-100 text-blue-800'
                                    }`}>
                                    {item.work_location}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600">
                                  {item.check_in_note || item.check_out_note || "-"}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-center gap-2">
                                    {userInfo?.role === 'admin' && (
                                      <button
                                        onClick={() => handleEditAttendance(item)}
                                        className="p-2.5 text-blue-600 hover:bg-blue-50 active:bg-blue-100 rounded-xl transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
                                        title="Edit"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleDeleteAttendance(item.id)}
                                      className="p-2.5 text-red-600 hover:bg-red-50 active:bg-red-100 rounded-xl transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
                                      title="Hapus"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Cards */}
                      <div className="md:hidden space-y-4">
                        {attendanceHistory.map((item) => (
                          <div key={item.id} className="bg-gradient-to-br from-slate-50 to-blue-50 p-4 rounded-xl border-2 border-gray-200 shadow-sm">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                {userInfo?.role === 'admin' && (
                                  <div className="mb-2">
                                    <p className="font-bold text-slate-900">{item.users?.full_name || 'N/A'}</p>
                                    <p className="text-xs text-slate-500">{item.users?.email || ''}</p>
                                  </div>
                                )}
                                <p className="font-semibold text-slate-800">
                                  {new Date(item.date).toLocaleDateString("id-ID", { weekday: 'long', day: 'numeric', month: 'long' })}
                                </p>
                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold mt-1 ${item.work_location === 'WFO' ? 'bg-blue-100 text-blue-800' : 'bg-blue-100 text-blue-800'
                                  }`}>
                                  {item.work_location}
                                </span>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-bold text-blue-600 mb-2">
                                  {calculateDuration(item.check_in, item.check_out)}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                              <div>
                                <p className="text-slate-600 text-xs">Check In</p>
                                <p className="font-semibold text-green-600">{item.check_in || "-"}</p>
                              </div>
                              <div>
                                <p className="text-slate-600 text-xs">Check Out</p>
                                <p className="font-semibold text-red-600">{item.check_out || "-"}</p>
                              </div>
                            </div>
                            {(item.check_in_note || item.check_out_note) && (
                              <div className="mb-3 pb-3 border-b border-gray-200">
                                <p className="text-xs text-slate-600">
                                  {item.check_in_note || item.check_out_note}
                                </p>
                              </div>
                            )}
                            {/* Action Buttons */}
                            <div className="flex gap-2 pt-2">
                              {userInfo?.role === 'admin' && (
                                <button
                                  onClick={() => handleEditAttendance(item)}
                                  className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 active:scale-95 text-white px-4 py-2.5 rounded-xl font-semibold transition-all shadow-sm min-h-[44px]"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Edit
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteAttendance(item.id)}
                                className={`${userInfo?.role === 'admin' ? 'flex-1' : 'w-full'} flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 active:scale-95 text-white px-4 py-2.5 rounded-xl font-semibold transition-all shadow-sm min-h-[44px]`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Hapus
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Timesheet Modal */}
                {showTimesheetModal && (
                  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[85vh] overflow-y-auto"
                    >
                      {/* Header */}
                      <div className="sticky top-0 bg-gradient-to-r from-blue-600 via-blue-600 to-pink-600 px-4 py-3 rounded-t-2xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                            <h2 className="text-base font-bold text-white">
                              {editingTimesheet ? "Edit Timesheet" : "Tambah Timesheet"}
                            </h2>
                          </div>
                          <button
                            onClick={() => {
                              setShowTimesheetModal(false);
                              setEditingTimesheet(null);
                              setTimesheetForm({
                                project_code: "",
                                project_name: "",
                                task_description: "",
                                task_list: [],
                                list_mode: false,
                                date: new Date().toISOString().split('T')[0]
                              });
                            }}
                            className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-lg transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4 space-y-3">
                        {/* Date Input */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                            Tanggal <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <input
                              type="date"
                              value={timesheetForm.date}
                              onChange={(e) => setTimesheetForm({ ...timesheetForm, date: e.target.value })}
                              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white cursor-pointer"
                              style={{ colorScheme: 'light' }}
                              required
                            />
                          </div>
                        </div>

                        {/* Project Code */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                            Kode Project <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                            </svg>
                            <input
                              type="text"
                              value={timesheetForm.project_code}
                              onChange={(e) => setTimesheetForm({ ...timesheetForm, project_code: e.target.value })}
                              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              placeholder="Contoh: [EXT-340]"
                              required
                            />
                          </div>
                        </div>

                        {/* Project Name */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                            Nama Project <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <input
                              type="text"
                              value={timesheetForm.project_name}
                              onChange={(e) => setTimesheetForm({ ...timesheetForm, project_name: e.target.value })}
                              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              placeholder="Nama lengkap project"
                              required
                            />
                          </div>
                        </div>

                        {/* Task Description */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-xs font-semibold text-gray-700">
                              Deskripsi Task <span className="text-red-500">*</span>
                            </label>
                            {/* Toggle List Mode */}
                            <button
                              type="button"
                              onClick={() => setTimesheetForm({
                                ...timesheetForm,
                                list_mode: !timesheetForm.list_mode,
                                task_list: timesheetForm.list_mode ? [] : ['']
                              })}
                              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${timesheetForm.list_mode
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                              </svg>
                              {timesheetForm.list_mode ? 'List Mode' : 'Add List'}
                            </button>
                          </div>

                          {/* Main Description */}
                          <textarea
                            value={timesheetForm.task_description}
                            onChange={(e) => setTimesheetForm({ ...timesheetForm, task_description: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                            rows="2"
                            placeholder={timesheetForm.list_mode ? "Deskripsi utama (opsional)..." : "Jelaskan apa yang dikerjakan..."}
                          ></textarea>

                          {/* Optional List Items */}
                          {timesheetForm.list_mode && (
                            <div className="mt-2 space-y-2">
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Tambahkan item list (bullet points)
                              </p>
                              {timesheetForm.task_list.map((item, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <span className="text-blue-500 font-bold">•</span>
                                  <input
                                    type="text"
                                    value={item}
                                    onChange={(e) => {
                                      const newList = [...timesheetForm.task_list];
                                      newList[index] = e.target.value;
                                      setTimesheetForm({ ...timesheetForm, task_list: newList });
                                    }}
                                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={`Item ${index + 1}...`}
                                  />
                                  {timesheetForm.task_list.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newList = timesheetForm.task_list.filter((_, i) => i !== index);
                                        setTimesheetForm({ ...timesheetForm, task_list: newList });
                                      }}
                                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => setTimesheetForm({
                                  ...timesheetForm,
                                  task_list: [...timesheetForm.task_list, '']
                                })}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Tambah Item
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="sticky bottom-0 bg-gray-50 px-4 py-3 rounded-b-2xl border-t border-gray-200">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setShowTimesheetModal(false);
                              setEditingTimesheet(null);
                              setTimesheetForm({
                                project_code: "",
                                project_name: "",
                                task_description: "",
                                task_list: [],
                                list_mode: false,
                                date: new Date().toISOString().split('T')[0]
                              });
                            }}
                            className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all text-sm"
                          >
                            Batal
                          </button>
                          <button
                            onClick={handleAddTimesheet}
                            disabled={isSubmittingTimesheet}
                            className={`flex-1 px-4 py-2 text-white rounded-lg font-semibold transition-all shadow-md text-sm ${isSubmittingTimesheet
                                ? "bg-blue-400 cursor-not-allowed"
                                : "bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 hover:shadow-lg"
                              }`}
                          >
                            {isSubmittingTimesheet ? "Menyimpan..." : (editingTimesheet ? "💾 Update" : "💾 Simpan")}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                )}

                {/* Admin Edit Modal */}
                {showAdminEditModal && (
                  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[75vh] overflow-y-auto"
                    >
                      {/* Header */}
                      <div className="sticky top-0 bg-gradient-to-r from-blue-600 via-blue-600 to-blue-600 px-4 py-3 rounded-t-2xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <div>
                              <h2 className="text-base font-bold text-white">
                                Edit Absensi
                              </h2>
                              {editingAttendance?.users && (
                                <p className="text-xs text-white/80">
                                  {editingAttendance.users.full_name}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setShowAdminEditModal(false);
                              setEditingAttendance(null);
                            }}
                            className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-lg transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4 space-y-3">
                        {/* User Info Warning (jika edit user lain) */}
                        {editingAttendance?.user_id !== userInfo.id && editingAttendance?.users && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <div className="text-sm">
                                <p className="font-semibold text-yellow-800">Perhatian!</p>
                                <p className="text-yellow-700 mt-1">
                                  Anda mengedit data absensi <span className="font-bold">{editingAttendance.users.full_name}</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Date Display */}
                        <div className="bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg p-2.5 border border-blue-100">
                          <div className="flex items-center gap-2 text-sm text-blue-800">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="font-semibold">
                              {new Date(adminEditForm.date).toLocaleDateString("id-ID", { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                          </div>
                        </div>

                        {/* Work Location */}
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                            Lokasi Kerja
                          </label>
                          <div className="relative">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <select
                              value={adminEditForm.work_location}
                              onChange={(e) =>
                                setAdminEditForm({ ...adminEditForm, work_location: e.target.value })
                              }
                              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                            >
                              <option value="WFO">🏢 WFO (Work From Office)</option>
                              <option value="WFH">🏠 WFH (Work From Home)</option>
                              <option value="WFA">🌍 WFA (Work From Anywhere)</option>
                            </select>
                          </div>
                        </div>

                        {/* Check-in Time & Note */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                              Check-in <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-green-500 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <input
                                type="time"
                                value={adminEditForm.check_in}
                                onChange={(e) =>
                                  setAdminEditForm({ ...adminEditForm, check_in: e.target.value })
                                }
                                className="w-full pl-2.5 sm:pl-10 pr-2.5 py-2 text-xs sm:text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all min-h-[40px] sm:min-h-[42px] bg-white"
                                required
                              />
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-green-500 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                              Check-out
                            </label>
                            <div className="relative">
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-red-500 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <input
                                type="time"
                                value={adminEditForm.check_out}
                                onChange={(e) =>
                                  setAdminEditForm({ ...adminEditForm, check_out: e.target.value })
                                }
                                className="w-full pl-2.5 sm:pl-10 pr-2.5 py-2 text-xs sm:text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all min-h-[40px] sm:min-h-[42px] bg-white"
                              />
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          </div>
                        </div>

                        {/* Notes */}
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                              Catatan Check-in
                            </label>
                            <textarea
                              value={adminEditForm.check_in_note}
                              onChange={(e) =>
                                setAdminEditForm({ ...adminEditForm, check_in_note: e.target.value })
                              }
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                              rows="2"
                              placeholder="Opsional..."
                            />
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                              Catatan Check-out
                            </label>
                            <textarea
                              value={adminEditForm.check_out_note}
                              onChange={(e) =>
                                setAdminEditForm({ ...adminEditForm, check_out_note: e.target.value })
                              }
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                              rows="2"
                              placeholder="Opsional..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="sticky bottom-0 bg-gray-50 px-4 py-3 rounded-b-2xl border-t border-gray-200">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setShowAdminEditModal(false);
                              setEditingAttendance(null);
                            }}
                            className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all text-sm"
                          >
                            Batal
                          </button>
                          <button
                            onClick={handleSaveAdminEdit}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg text-sm"
                          >
                            💾 Simpan
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                )}

                {/* Print Content (Hidden) */}
                <div className="hidden">
                  <div ref={printRef} className="p-6 bg-white">
                    {/* Header dengan Logo */}
                    <div className="flex justify-between items-start mb-4 pb-3 border-b-2 border-orange-500">
                      {/* Logo Kiri - ADIDATA */}
                      <div className="flex items-center">
                        <img
                          src="/assets/logos/adidata.png"
                          alt="PT ADI DATA INFORMATIKA"
                          className="h-14 object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>

                      {/* Title Center */}
                      <div className="text-center flex-1 px-4">
                        <h1 className="text-lg font-bold text-gray-800">
                          PT ADI DATA INFORMATIKA - Daily Attendance Report
                        </h1>
                      </div>

                      {/* Logo Kanan - BSI */}
                      <div className="flex items-center">
                        <img
                          src="/assets/logos/logobsi.png"
                          alt="Bank Syariah Indonesia"
                          className="h-14 object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    </div>

                    {/* Info Karyawan */}
                    <div className="mb-4 text-sm">
                      <table className="text-gray-700">
                        <tbody>
                          <tr>
                            <td className="pr-2 font-semibold">Name</td>
                            <td className="pr-2">:</td>
                            <td className="text-blue-600 font-medium">
                              {selectedUserForPrint?.full_name || userInfo?.full_name || 'All Users'}
                            </td>
                          </tr>
                          <tr>
                            <td className="pr-2 font-semibold">Role</td>
                            <td className="pr-2">:</td>
                            <td>{selectedUserForPrint?.position || userInfo?.position || 'Employee'}</td>
                          </tr>
                          <tr>
                            <td className="pr-2 font-semibold">Period</td>
                            <td className="pr-2">:</td>
                            <td>
                              {(() => {
                                const data = filteredAttendanceForPrint.length > 0 ? filteredAttendanceForPrint : [];
                                const dates = data.map(item => new Date(item.date)).sort((a, b) => a - b);
                                if (dates.length === 0) return '-';
                                const start = dates[0].toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
                                const end = dates[dates.length - 1].toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
                                return `${start} - ${end}`;
                              })()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Tabel Data */}
                    <table className="w-full border-collapse text-xs">
                      <thead>
                        <tr className="bg-orange-500 text-white">
                          <th className="border border-gray-400 px-2 py-1.5 text-center">No</th>
                          {(userInfo?.role === 'admin' && !selectedUserForPrint) && (
                            <th className="border border-gray-400 px-2 py-1.5 text-left">Nama</th>
                          )}
                          <th className="border border-gray-400 px-2 py-1.5 text-center">Hari</th>
                          <th className="border border-gray-400 px-2 py-1.5 text-left">Tanggal</th>
                          <th className="border border-gray-400 px-2 py-1.5 text-center" colSpan="2">Time</th>
                          <th className="border border-gray-400 px-2 py-1.5 text-center">Durasi</th>
                          <th className="border border-gray-400 px-2 py-1.5 text-center">Lokasi</th>
                          <th className="border border-gray-400 px-2 py-1.5 text-left">Keterangan</th>
                        </tr>
                        <tr className="bg-orange-400 text-white text-[10px]">
                          <th className="border border-gray-400 px-1 py-0.5"></th>
                          {(userInfo?.role === 'admin' && !selectedUserForPrint) && (
                            <th className="border border-gray-400 px-1 py-0.5"></th>
                          )}
                          <th className="border border-gray-400 px-1 py-0.5"></th>
                          <th className="border border-gray-400 px-1 py-0.5"></th>
                          <th className="border border-gray-400 px-1 py-0.5 text-center">From</th>
                          <th className="border border-gray-400 px-1 py-0.5 text-center">To</th>
                          <th className="border border-gray-400 px-1 py-0.5"></th>
                          <th className="border border-gray-400 px-1 py-0.5"></th>
                          <th className="border border-gray-400 px-1 py-0.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAttendanceForPrint.length > 0 ? (
                          filteredAttendanceForPrint.map((item, index) => (
                            <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">{index + 1}</td>
                              {(userInfo?.role === 'admin' && !selectedUserForPrint) && (
                                <td className="border border-gray-300 px-2 py-1.5">{item.users?.full_name || 'N/A'}</td>
                              )}
                              <td className="border border-gray-300 px-2 py-1.5 text-center">
                                {new Date(item.date).toLocaleDateString('id-ID', { weekday: 'long' })}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5">
                                {new Date(item.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">{item.check_in || '-'}</td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">{item.check_out || '-'}</td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">
                                {calculateDuration(item.check_in, item.check_out)}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">{item.work_location}</td>
                              <td className="border border-gray-300 px-2 py-1.5 text-[10px]">
                                {item.check_in_note || item.check_out_note || '-'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={(userInfo?.role === 'admin' && !selectedUserForPrint) ? 9 : 8}
                              className="border border-gray-300 px-4 py-8 text-center text-gray-500"
                            >
                              Tidak ada data absensi
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Footer */}
                    <div className="mt-4 pt-3 border-t text-xs text-gray-500 flex justify-between">
                      <div>
                        Total: {filteredAttendanceForPrint.length} data |
                        Hadir: {filteredAttendanceForPrint.filter(i => i.check_in).length}
                      </div>
                      <div>
                        Generated: {new Date().toLocaleString('id-ID')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Pemilihan User untuk Export */}
                {showExportFilterModal && (
                  <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden"
                    >
                      {/* Header */}
                      <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 text-white">
                        <h3 className="text-xl font-bold">📊 Pilih User untuk Export</h3>
                        <p className="text-green-100 text-sm mt-1">Pilih karyawan atau export semua data</p>
                      </div>

                      {/* Content */}
                      <div className="p-6 max-h-[60vh] overflow-y-auto">
                        {/* Option: Export Semua User */}
                        <button
                          onClick={() => {
                            executeExport(null);
                          }}
                          className="w-full p-4 mb-3 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 border-2 border-green-200 hover:border-green-400 rounded-xl text-left transition-all group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg">
                              👥
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-gray-800 group-hover:text-green-600 transition-colors">
                                Semua Karyawan
                              </p>
                              <p className="text-sm text-gray-500">
                                {attendanceHistory.length} data absensi
                              </p>
                            </div>
                            <ArrowDownTrayIcon className="w-5 h-5 text-gray-400 group-hover:text-green-600 transition-colors" />
                          </div>
                        </button>

                        {/* List Users */}
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-gray-600 mb-2">Atau pilih karyawan:</p>
                          {allUsers.map((user) => {
                            const userAttendanceCount = attendanceHistory.filter(a => a.user_id === user.id).length;
                            return (
                              <button
                                key={user.id}
                                onClick={() => {
                                  executeExport(user);
                                }}
                                className="w-full p-3 bg-gray-50 hover:bg-green-50 border border-gray-200 hover:border-green-300 rounded-lg text-left transition-all group"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-semibold text-sm">
                                    {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U'}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-800 group-hover:text-green-600 transition-colors truncate">
                                      {user.full_name}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {user.email} • {userAttendanceCount} data
                                    </p>
                                  </div>
                                  <ArrowDownTrayIcon className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors flex-shrink-0" />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="bg-gray-50 px-6 py-4 flex gap-3">
                        <button
                          onClick={() => {
                            setShowExportFilterModal(false);
                            setSelectedUserForExport(null);
                          }}
                          className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                        >
                          Batal
                        </button>
                      </div>
                    </motion.div>
                  </div>
                )}

                {/* Modal Pemilihan User untuk Print */}
                {showUserFilterModal && (
                  <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden"
                    >
                      {/* Header */}
                      <div className="bg-gradient-to-r from-blue-600 to-blue-600 px-6 py-4 text-white">
                        <h3 className="text-xl font-bold">📄 Pilih User untuk Print</h3>
                        <p className="text-blue-100 text-sm mt-1">Pilih karyawan atau print semua data</p>
                      </div>

                      {/* Content */}
                      <div className="p-6 max-h-[60vh] overflow-y-auto">
                        {/* Option: Print Semua User */}
                        <button
                          onClick={() => {
                            executePrint(null);
                          }}
                          className="w-full p-4 mb-3 bg-gradient-to-r from-blue-50 to-blue-50 hover:from-blue-100 hover:to-blue-100 border-2 border-blue-200 hover:border-blue-400 rounded-xl text-left transition-all group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                              👥
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors">
                                Semua Karyawan
                              </p>
                              <p className="text-sm text-gray-500">
                                {attendanceHistory.length} data absensi
                              </p>
                            </div>
                            <PrinterIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                          </div>
                        </button>

                        {/* List Users */}
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-gray-600 mb-2">Atau pilih karyawan:</p>
                          {allUsers.map((user) => {
                            const userAttendanceCount = attendanceHistory.filter(a => a.user_id === user.id).length;
                            return (
                              <button
                                key={user.id}
                                onClick={() => {
                                  executePrint(user);
                                }}
                                className="w-full p-3 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg text-left transition-all group"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-pink-500 flex items-center justify-center text-white font-semibold text-sm">
                                    {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U'}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors truncate">
                                      {user.full_name}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {user.email} • {userAttendanceCount} data
                                    </p>
                                  </div>
                                  <PrinterIcon className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="bg-gray-50 px-6 py-4 flex gap-3">
                        <button
                          onClick={() => {
                            setShowUserFilterModal(false);
                            setSelectedUserForPrint(null);
                          }}
                          className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                        >
                          Batal
                        </button>
                      </div>
                    </motion.div>
                  </div>
                )}

                {/* Toast */}
                {toastMessage && (
                  <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
                    <div
                      className={`px-6 py-3 rounded-lg shadow-lg ${toastType === "error"
                        ? "bg-red-500 text-white"
                        : "bg-green-500 text-white"
                        }`}
                    >
                      {toastMessage}
                    </div>
                  </div>
                )}
              </div>
            </main>
            <Footer />
          </>
        )}
      </div>
    </>
  );
}
